import { useTranslation } from 'next-i18next'
import qs from 'qs'
import { getErrorLabel } from '@captaincause/lib/api/errors'
import { API_URL } from '@captaincause/lib/api/url'
import { useMutation } from '@captaincause/lib/api/useMutation'
import { mustBeEmailAddress } from '@captaincause/lib/form/mustBeEmailAddress'
import { mustBeRequired } from '@captaincause/lib/form/mustBeRequired'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { Submit } from '@captaincause/ui/Button/Submit'
import { Form } from '@captaincause/ui/Form'
import { TextInputField } from '@captaincause/ui/TextInput/Field'
import { Toaster } from '@captaincause/ui/Toaster'
import { SmallFooterLayout } from 'layouts/SmallFooter'
import { DifterPageHead } from 'pages/Head'
import { injectI18n } from 'utils/i18n'
import { Button } from 'components/Button'
import { CaptainNav } from 'components/CaptainNav'
import { GoogleSignIn } from '../GoogleSignIn'
import { useSignIn } from '../utils'
import type { ThrowableApiErrorData } from '@captaincause/lib/api/errors'
import type {
  FormMount,
  FormSubmit,
  FormSubmitSuccess,
} from '@captaincause/ui/Form'
import type { NextPageWithLayout } from 'app'
import type { GetStaticProps, InferGetStaticPropsType } from 'next'
import type { RegisteredUser } from 'types/user'

interface PageProps {}

export const getStaticProps: GetStaticProps<PageProps> = async context => {
  const i18n = await injectI18n(context.locale, [
    'footer.component',
    'me.page',
    'nav.component',
    'metadata',
  ])

  return {
    props: { ...i18n },
  }
}

// ---

interface LoginFormValues {
  email: string
  password: string
}

interface LoginTriggerArgs {
  email: string
  password: string
}

interface GoogleLoginTriggerArgs {
  google_token: string
}

type InferredPageProps = InferGetStaticPropsType<typeof getStaticProps>
const Login: NextPageWithLayout<InferredPageProps> = (props: PageProps) => {
  const { t } = useTranslation(['me.page', 'common'])

  const locale = useNextLocale()

  const { track } = useMixpanel()

  const signIn = useSignIn()

  const { trigger: triggerLogin } = useMutation<
    RegisteredUser,
    LoginTriggerArgs | GoogleLoginTriggerArgs
  >({
    url: `${API_URL}/give/users/authentication/login`,
    method: 'POST',
  })

  const submit: FormSubmit<LoginFormValues, RegisteredUser> = async values => {
    track('Dift Account - Log in')

    const registeredUser = await triggerLogin({
      email: values.email,
      password: values.password,
    })

    return registeredUser
  }

  const submitSuccess: FormSubmitSuccess<
    LoginFormValues,
    RegisteredUser
  > = async registeredUser => {
    track('Dift Account - Log in success')

    await signIn(registeredUser)
  }

  const onGoogleSignInSuccess = async (credentials: string) => {
    try {
      const registeredUser = await triggerLogin({
        google_token: credentials,
      })

      track('Dift Account - Google log in success')

      await signIn(registeredUser)
    } catch (error: unknown) {
      const castedError = error as ThrowableApiErrorData

      Toaster({
        type: 'error',
        message: getErrorLabel(castedError?.data?.code, locale),
        id: 'GSignIn-register',
      })
    }
  }

  const onFormMount: FormMount<LoginFormValues> = form => {
    const params = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    })

    form.form.reset({
      email: typeof params.email === 'string' ? params.email : '',
    })
  }

  const initialValues: LoginFormValues = {
    email: '',
    password: '',
  }

  return (
    <>
      <DifterPageHead noIndex />

      <CaptainNav logoHref="/me" />

      <main
        id="main"
        className="mx-auto mt-14 flex w-full max-w-[500px] flex-1 flex-col items-center rounded-2xl px-4 py-6 md:my-24 md:border md:border-inside md:px-8 md:py-8"
      >
        <h1 className="mb-10 text-center font-default text-3xl font-bold">
          {t('login.form.title')}
        </h1>

        <div className="flex items-center justify-center">
          <GoogleSignIn
            mode="signin"
            onSuccess={onGoogleSignInSuccess}
            locale={locale}
          />
        </div>

        <hr className="my-8 w-full text-medium-gray" />

        <Form<LoginFormValues, RegisteredUser>
          className="mb-6 w-full"
          onSubmit={submit}
          onSubmitSuccess={submitSuccess}
          onMount={onFormMount}
          initialValues={initialValues}
          submitDelay={2000}
          withToastOnFailure
          locale={locale}
        >
          <div className="mb-8 flex w-full flex-col space-y-4">
            <TextInputField
              id="email"
              name="email"
              placeholder={t('common:email-placeholder')}
              label={t('common:email')}
              locale={locale}
              validators={{
                mustBeRequired,
                mustBeEmailAddress,
              }}
              autoComplete="email"
            />

            <TextInputField
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              label={t('common:password')}
              locale={locale}
              validators={{
                mustBeRequired,
              }}
              autoComplete="current-password"
            />
          </div>
          <div className="mb-8 mt-1 flex w-full justify-end">
            <Button
              variant="tertiary"
              href="/me/forgot"
              inline
              className="text-sm no-underline hover:bg-transparent hover:underline active:bg-transparent"
            >
              {t('forgot.form.cta')}
            </Button>
          </div>
          <Submit variant="primary" full>
            {t('login.form.submit')}
          </Submit>
        </Form>
        <div className="text-center text-dark-gray">
          {t('login.form.no-account')}
          <Button variant="tertiary" href="/me/register" inline>
            {t('login.form.signup')}
          </Button>
        </div>
      </main>
    </>
  )
}

Login.Layout = SmallFooterLayout

export default Login
